/**
 * Comparables en el mapa principal (T3): geometría y textos, funciones puras.
 *
 * - Círculo del radio y zonas de ~100 m (`pin_approx`) como polígonos GeoJSON armados a mano
 *   (aproximación equirectangular: a 3 km el error es despreciable para dibujar). Sin turf.
 * - Puntos de chips con la forma de `groupRecordsForMap` (`record_key` = `comparables[].key`),
 *   para el layout con clúster de `layoutRecordChips` (mismo patrón que la lista compartida).
 * - Textos de fila / tooltip. Nunca la fuente del aviso ni `debug` (el normalizador no los deja
 *   pasar y acá solo se leen campos conocidos).
 * Ver docs/maps/market-comparison.md («Modo Comparables»).
 */
import { groupRecordsForMap } from '../Map/utils/recordCards';
import {
  formatMarketMonthly,
  formatMarketNumber,
  formatMarketPerM2,
  formatMarketRadius,
} from './marketComparison';

/** Radio de la zona punteada de un comparable con ubicación aproximada (3 decimales ≈ 100 m). */
export const MARKET_APPROX_RADIUS_M = 100;
/** Chip de `very_similar: true` (reemplaza al de `match_label`). */
export const MARKET_VERY_SIMILAR_LABEL = 'Muy parecida';
/** Filas del tooltip de un clúster; el resto va como «y N más». */
export const MARKET_CLUSTER_TOOLTIP_MAX = 8;
/** Metros por grado de latitud. */
const M_PER_DEG_LAT = 111320;

const finite = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** `{ lat, lng }` válido o null (acepta strings numéricos). */
export function normalizeMarketCenter(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const lat = finite(Number(raw.lat));
  const lng = finite(Number(raw.lng ?? raw.lon));
  if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  if (raw.lat === null || raw.lat === '' || (raw.lng ?? raw.lon) === null || (raw.lng ?? raw.lon) === '') return null;
  return { lat, lng };
}

const degDeltas = (lat, radiusM) => {
  const dLat = radiusM / M_PER_DEG_LAT;
  const cos = Math.cos((lat * Math.PI) / 180);
  const dLng = radiusM / (M_PER_DEG_LAT * Math.max(cos, 0.01));
  return { dLat, dLng };
};

/**
 * Anillo cerrado `[[lng, lat], …]` de un círculo de `radiusM` metros.
 * @returns {number[][]|null}
 */
export function circleRing(center, radiusM, steps = 64) {
  const c = normalizeMarketCenter(center);
  const r = finite(Number(radiusM));
  if (!c || r === null || r <= 0) return null;
  const n = Math.max(8, Math.floor(steps));
  const { dLat, dLng } = degDeltas(c.lat, r);
  const ring = [];
  for (let i = 0; i < n; i += 1) {
    const a = (i / n) * Math.PI * 2;
    ring.push([c.lng + dLng * Math.cos(a), c.lat + dLat * Math.sin(a)]);
  }
  ring.push(ring[0]);
  return ring;
}

/** Caja `[[oeste, sur], [este, norte]]` del círculo (para `fitBounds`). */
export function circleBounds(center, radiusM) {
  const c = normalizeMarketCenter(center);
  const r = finite(Number(radiusM));
  if (!c || r === null || r <= 0) return null;
  const { dLat, dLng } = degDeltas(c.lat, r);
  return [[c.lng - dLng, c.lat - dLat], [c.lng + dLng, c.lat + dLat]];
}

/** Centro (de la caja) de una geometría GeoJSON Point / Polygon / MultiPolygon. */
export function geometryCenter(geometry) {
  if (!geometry || !geometry.coordinates) return null;
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  const walk = (coords) => {
    if (!Array.isArray(coords)) return;
    if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      const [x, y] = coords;
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
      return;
    }
    coords.forEach(walk);
  };
  walk(geometry.coordinates);
  if (!Number.isFinite(minX)) return null;
  return normalizeMarketCenter({ lat: (minY + maxY) / 2, lng: (minX + maxX) / 2 });
}

const polygonFeature = (ring, properties = {}) => ({
  type: 'Feature',
  properties,
  geometry: { type: 'Polygon', coordinates: [ring] },
});

/** Círculo del radio de búsqueda (vacío si no hay centro o radio). */
export function buildMarketRadiusGeoJSON(center, radiusM) {
  const ring = circleRing(center, radiusM, 96);
  return { type: 'FeatureCollection', features: ring ? [polygonFeature(ring, { kind: 'radius' })] : [] };
}

/** Zonas punteadas de ~100 m para los comparables con `pin_approx` (una por punto). */
export function buildMarketApproxGeoJSON(comparables) {
  const seen = new Set();
  const features = [];
  (Array.isArray(comparables) ? comparables : []).forEach((c) => {
    if (!c?.pin || !c.pinApprox) return;
    const pointKey = `${c.pin.lat.toFixed(4)},${c.pin.lng.toFixed(4)}`;
    if (seen.has(pointKey)) return;
    seen.add(pointKey);
    const ring = circleRing(c.pin, MARKET_APPROX_RADIUS_M, 40);
    if (ring) features.push(polygonFeature(ring, { kind: 'approx', key: c.key }));
  });
  return { type: 'FeatureCollection', features };
}

/**
 * Ancho del chip de comparable (px, con 6 px de padding y borde). Medido con Inter 700 11,5 px:
 * venta hasta «USD 12.500/m²» (86 px de texto); alquiler (total por mes) hasta
 * «ARS 1.250.000/mes» (112 px).
 * Si un valor no entra, la pastilla corta con «…» (CSS) sin cambiar de tamaño.
 */
export const MARKET_CHIP_WIDTH = Object.freeze({ venta: 102, alquiler: 128 });
export const marketChipWidth = (operation) => (operation === 'alquiler' ? MARKET_CHIP_WIDTH.alquiler : MARKET_CHIP_WIDTH.venta);

/**
 * Texto del chip con moneda y unidad, mismo número que la lista (`formatMarketNumber`, un
 * decimal debajo de 100): venta «USD 1.020/m²»; alquiler «USD 11,5/m²·mes» o «$ 9.900/m²·mes»
 * (pesos). Sin precio: «—».
 */
export function formatComparableChip(value, { currency = null, operation = null } = {}) {
  const v = finite(Number(value));
  if (v === null || v <= 0 || value === null || value === '') return '—';
  const rent = operation === 'alquiler';
  const cur = currency || (rent ? 'ARS' : 'USD');
  const sign = cur === 'ARS' ? '$' : cur;
  return `${sign} ${formatMarketNumber(v)}/m²${rent ? '·mes' : ''}`;
}

/**
 * Chip de un comparable. Alquiler con total: «ARS 620.000/mes» (decisión 30 oct: el total por
 * mes, no el /m²). Sin total, el /m² de `formatComparableChip`. Venta: /m².
 */
export function comparableChipLabel(c, block = null) {
  const currency = c?.currency || block?.currency || null;
  const operation = c?.operation || block?.operation || null;
  if (operation === 'alquiler') {
    const total = finite(Number(c?.priceTotal));
    if (c?.priceTotal !== null && c?.priceTotal !== undefined && total !== null && total > 0) {
      return formatMarketMonthly(total, currency || 'ARS');
    }
  }
  return formatComparableChip(c?.pricePerM2, { currency, operation });
}

/**
 * Puntos para `layoutRecordChips`: un registro por comparable con pin; los que caen en la
 * misma coordenada (aproximados redondeados) van en abanico.
 */
export function buildComparableMapPoints(comparables, block = null) {
  const records = (Array.isArray(comparables) ? comparables : [])
    .filter((c) => c?.key && c.pin)
    .map((c) => ({
      record_key: c.key,
      group_key: 'market-comparables',
      lat: c.pin.lat,
      lng: c.pin.lng,
      label: comparableChipLabel(c, block),
      hasPrice: c.pricePerM2 !== null || ((c.operation || block?.operation) === 'alquiler' && c.priceTotal !== null),
      approx: Boolean(c.pinApprox),
      comparable: c,
    }));
  return groupRecordsForMap(records);
}

/**
 * Distancia de la fila / popup: «A 200 m», «A 1,2 km» (con «A» para que no se lea como m²;
 * `midLine` → «a 200 m» si va en medio de una línea). Sin pin → '' (la fila dice «sin
 * ubicación»); 0 → «mismo edificio».
 */
export function formatComparableDistance(c, { midLine = false } = {}) {
  if (!c?.pin) return '';
  const d = finite(Number(c.distanceM));
  if (d === null) return '';
  if (d === 0) return 'mismo edificio';
  const value = d >= 1000 ? formatMarketRadius(d) : `${Math.round(d)} m`;
  return `${midLine ? 'a' : 'A'} ${value}`;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Total: «USD 113.000» (venta) o «ARS 520.000/mes» (alquiler). */
export function formatComparableTotal(c) {
  if (!c || c.priceTotal === null || c.priceTotal === undefined) return '';
  const n = formatMarketNumber(c.priceTotal);
  if (!n) return '';
  const cur = c.currency || (c.operation === 'alquiler' ? 'ARS' : 'USD');
  return `${cur} ${n}${c.priceTotalPeriod === 'mes' || c.operation === 'alquiler' ? '/mes' : ''}`;
}

/**
 * Textos de una fila / tooltip de comparable.
 * @param {object} c - comparable normalizado
 * @param {{ currency?: string|null, operation?: string|null }} [block]
 */
export function describeComparable(c, block = {}) {
  const operation = c?.operation || block?.operation || null;
  const currency = c?.currency || block?.currency || null;
  const perM2 = formatMarketPerM2(c?.pricePerM2, currency, operation);
  const surface = c?.surfaceM2 !== null && c?.surfaceM2 !== undefined ? `${formatMarketNumber(c.surfaceM2)} m²` : '';
  const total = formatComparableTotal({ ...c, currency, operation });
  // Alquiler (decisión 30 oct, Ignacio): título = total por mes; debajo solo «43 m²» (sin /m²).
  const rentTotal = operation === 'alquiler' && Boolean(total);
  const attrs = [
    c?.bedrooms !== null && c?.bedrooms !== undefined
      ? (c.bedrooms === 0 ? 'monoambiente' : plural(c.bedrooms, 'dorm', 'dorm'))
      : '',
    c?.bathrooms ? plural(c.bathrooms, 'baño', 'baños') : '',
    c?.rooms ? `${c.rooms} amb` : '',
    c?.parking ? plural(c.parking, 'cochera', 'cocheras') : '',
  ].filter(Boolean).join(' · ');
  const location = !c?.pin ? 'sin ubicación' : (c.pinApprox ? 'ubic. aprox.' : '');
  const meta = [formatComparableDistance(c), c?.barrio || '', c?.dateLabel || '', location]
    .filter(Boolean).join(' · ');
  return {
    perM2,
    title: rentTotal ? total : perM2,
    sizeTotal: (rentTotal ? [surface] : [surface, total]).filter(Boolean).join(' · '),
    attrs,
    meta,
    location,
    match: c?.match || null,
    matchLabel: c?.matchLabel || '',
    verySimilar: Boolean(c?.verySimilar),
    // Un solo chip por fila: «Muy parecida» reemplaza al de `match_label`.
    chipLabel: c?.verySimilar ? MARKET_VERY_SIMILAR_LABEL : (c?.matchLabel || ''),
    photos: Array.isArray(c?.photos) ? c.photos : [],
  };
}

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/**
 * Tooltip del chip (clic): foto si hay, «USD 1.950/m² · 58 m²», total y atributos. Alquiler:
 * «ARS 620.000/mes», «43 m² · atributos».
 */
export function buildComparableTooltipHtml(c, block = {}) {
  if (!c) return null;
  const d = describeComparable(c, block);
  const surface = c.surfaceM2 !== null && c.surfaceM2 !== undefined ? `${formatMarketNumber(c.surfaceM2)} m²` : '';
  const rows = [];
  const photo = d.photos[0];
  if (photo && /^https:\/\//i.test(photo)) {
    rows.push(`<img class="terrio-record-chip-tooltip__cover" src="${escapeHtml(photo)}" alt="" loading="lazy" referrerpolicy="no-referrer" />`);
  }
  const rentTotal = d.title !== d.perM2;
  // Alquiler: «ARS 620.000/mes» y debajo «43 m² · 1 dorm». Venta: igual que antes.
  const title = rentTotal ? d.title : [d.perM2, surface].filter(Boolean).join(' · ');
  rows.push(`<div class="terrio-cad-share-tooltip__title">${escapeHtml(title)}</div>`);
  if (d.verySimilar) rows.push(`<div class="terrio-market-tooltip-tag">${escapeHtml(MARKET_VERY_SIMILAR_LABEL)}</div>`);
  const second = (rentTotal
    ? [d.sizeTotal, d.attrs]
    : [formatComparableTotal({ ...c, currency: c.currency || block.currency, operation: c.operation || block.operation }), d.attrs])
    .filter(Boolean).join(' · ');
  if (second) rows.push(`<div class="terrio-cad-share-tooltip__muted">${escapeHtml(second)}</div>`);
  const third = [formatComparableDistance(c), d.location].filter(Boolean).join(' · ');
  if (third) rows.push(`<div class="terrio-cad-share-tooltip__muted">${escapeHtml(third)}</div>`);
  return `<div class="terrio-cad-share-tooltip__card" data-comparable-key="${escapeHtml(c.key)}">${rows.join('')}</div>`;
}

/**
 * Tooltip de un clúster: los precios de adentro (mismo texto del chip), sin foto.
 * No mueve el mapa; el clic del clúster sigue acercando.
 */
export function buildClusterTooltipHtml(comparables, recordKeys, block = {}) {
  const keys = Array.isArray(recordKeys) ? recordKeys : [];
  const byKey = new Map((Array.isArray(comparables) ? comparables : []).filter((c) => c?.key).map((c) => [c.key, c]));
  const items = keys.map((k) => byKey.get(k)).filter(Boolean);
  if (!items.length) return null;
  const shown = items.slice(0, MARKET_CLUSTER_TOOLTIP_MAX);
  const extra = items.length - shown.length;
  const rows = shown.map((c) => {
    const price = comparableChipLabel(c, block);
    const tag = c.verySimilar
      ? `<span class="terrio-market-tooltip-tag">${escapeHtml(MARKET_VERY_SIMILAR_LABEL)}</span>`
      : '';
    return `<div class="terrio-market-cluster-tooltip__row" data-comparable-key="${escapeHtml(c.key)}"><span class="terrio-cad-share-tooltip__price">${escapeHtml(price)}</span>${tag}</div>`;
  });
  if (extra > 0) {
    rows.push(`<div class="terrio-cad-share-tooltip__muted">y ${extra} más</div>`);
  }
  return `<div class="terrio-cad-share-tooltip__card terrio-market-cluster-tooltip">${rows.join('')}</div>`;
}

/**
 * ¿El modo Comparables perdió su ficha de origen? (MapPage lo limpia sin restaurar encuadre.)
 * Dock: cambió o se cerró el lote abierto. Listas (parcela de showroom): se cerró Listas o se
 * abrió un lote en el Dock. Logout: siempre.
 */
export function shouldExitMarketComparables(view, { selectedPolygonId = null, isListsOpen = false, isLoggedIn = true } = {}) {
  if (!view) return false;
  if (!isLoggedIn) return true;
  if (view.source === 'lists') return !isListsOpen || selectedPolygonId != null;
  return String(selectedPolygonId ?? '') !== String(view.anchorId ?? '');
}

/**
 * Tooltip de un comparable: anclas posibles, en orden de preferencia. Son las cuatro con flecha en
 * `Map.css` (`.terrio-parcel-tooltip.maplibregl-popup-anchor-*`): `bottom` = arriba del chip
 * (como siempre), `top` = abajo, `left` = a la derecha, `right` = a la izquierda.
 */
export const COMPARABLE_TOOLTIP_ANCHORS = Object.freeze(['bottom', 'top', 'left', 'right']);

const tooltipBox = (anchor, point, offset, size) => {
  const x = point.x + offset[0];
  const y = point.y + offset[1];
  const { width: w, height: h } = size;
  if (anchor === 'top') return { left: x - w / 2, top: y, right: x + w / 2, bottom: y + h };
  if (anchor === 'left') return { left: x, top: y - h / 2, right: x + w, bottom: y + h / 2 };
  if (anchor === 'right') return { left: x - w, top: y - h / 2, right: x, bottom: y + h / 2 };
  return { left: x - w / 2, top: y - h, right: x + w / 2, bottom: y };
};

/**
 * Ancla del tooltip según el espacio libre: la vista del mapa menos lo que tapan el header de la
 * app / del mapa y el Dock (el mismo padding de cámara de la ficha). Si el ancla actual todavía
 * entra se mantiene (sin parpadeo al mover el mapa); si no, la primera que entra en el orden
 * arriba → abajo → derecha → izquierda; si ninguna entra, la que menos se sale.
 * @param {{ point: { x: number, y: number }, size: { width: number, height: number },
 *   view: { width: number, height: number }, padding?: { top?: number, right?: number,
 *   bottom?: number, left?: number }, offsets: Record<string, [number, number]>,
 *   current?: string|null }} args
 * @returns {'bottom'|'top'|'left'|'right'}
 */
export function pickComparableTooltipAnchor({ point, size, view, padding = {}, offsets, current = null }) {
  const area = {
    left: Number(padding.left) || 0,
    top: Number(padding.top) || 0,
    right: (view?.width || 0) - (Number(padding.right) || 0),
    bottom: (view?.height || 0) - (Number(padding.bottom) || 0),
  };
  const overflow = (anchor) => {
    const box = tooltipBox(anchor, point, offsets[anchor] || [0, 0], size);
    return Math.max(0, area.left - box.left) + Math.max(0, box.right - area.right)
      + Math.max(0, area.top - box.top) + Math.max(0, box.bottom - area.bottom);
  };
  if (!point || !size || !view?.width || !view?.height) return 'bottom';
  if (current && COMPARABLE_TOOLTIP_ANCHORS.includes(current) && overflow(current) === 0) return current;
  const fits = COMPARABLE_TOOLTIP_ANCHORS.find((anchor) => overflow(anchor) === 0);
  if (fits) return fits;
  return COMPARABLE_TOOLTIP_ANCHORS.reduce((best, anchor) => (overflow(anchor) < overflow(best) ? anchor : best));
}

